import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { Redirect } from 'react-router';
import { NavLink, useParams, Route, Switch, useHistory } from 'react-router-dom';
import { ConfirmButton, BackIcon, LogoSegment } from './ui';

import {
    Grid, Menu, Comment, Form, Button, Segment, Message, Input, TextArea, Icon,
    Popup, Visibility, Label, Loader, Responsive, Header, Transition, Confirm,
} from 'semantic-ui-react';

import {
    isAuthed, getConversations, getMessages, getAuthUserId, getConversation,
    getConversationMessages, getSearchUsers, getUnreadChatCount, getProfile,
    getChatOnline, getChatError,
} from '../selectors';

import {
    sendChatMessage, openChatConversation, closeChatConversation,
    createConversation, updateLastSeenMessage, fetchProfile, removeConversation,
    deleteChat, refreshChatMessages, unlockFreeConversation, clearChatError
} from "../actions";

import {
    timeSince, DivRef, isPresent, isDefined,
} from "../helpers";

// Every refusal the channels can send, in place of the old blanket jump to
// /subscribe. A member who is allowed to write must not be thrown out of the
// chat just because this one conversation is not theirs to read.
const CHAT_ERROR_TEXTS = {
    conversation_locked:
        "Dieses Gespräch ist noch verschwommen. Öffne es gratis oder schließe " +
        "ein Abo ab, um es zu lesen und zu beantworten.",
    gift_spent:
        "Dein freies Gespräch läuft schon mit jemand anderem. Mit einem Abo " +
        "kannst du alle Gespräche lesen und beantworten.",
    partner_unavailable:
        "Diese Person nutzt ihr freies Gespräch bereits mit jemand anderem und " +
        "könnte dir hier nicht antworten. Dein freies Gespräch bleibt dir " +
        "erhalten. Mit einem Abo kannst du trotzdem lesen und antworten.",
    free_daily_limit:
        "Du hast heute schon drei neue Gespräche begonnen. Morgen geht es weiter.",
    free_total_limit:
        "Du hast alle kostenlosen Anschreiben genutzt. Mit einem Abo kannst du " +
        "weiter schreiben.",
    profile_incomplete:
        "Bitte vervollständige zuerst dein Profil — Nickname, Ort und Foto.",
    message_required:
        "Schreibe eine Nachricht, um das Gespräch zu beginnen.",
    conversation_deleted:
        "Dieses Gespräch gibt es nicht mehr.",
    not_connected:
        "Keine Verbindung zu diesem Gespräch. Bitte lade die Seite neu und " +
        "sende die Nachricht noch einmal.",
};

const chatErrorText = (code) => CHAT_ERROR_TEXTS[code] ||
    "Das hat leider nicht geklappt. Bitte versuche es später erneut.";

// Refusals a subscription actually removes. Deliberately not all of them:
// pointing somebody at the subscription page when they only need to upload a
// photo, or when they left the message box empty, sells them something that
// will not help.
const SUBSCRIPTION_SOLVES = [
    'conversation_locked', 'gift_spent', 'partner_unavailable',
    'free_daily_limit', 'free_total_limit',
];

const makeConversationTitle = (conv, curUserId, link=false) => {
    const participants = conv.get('participants');
    let user = null;
    if(participants && participants.size > 1) {
        const otherParticipants = participants.filterNot(
            u => u.get('slug') === curUserId
        );
        user = otherParticipants.first();
    } else if(participants && participants.size > 0) {
        user = participants.first()
    }

    if (user === null) {
        // should not happen, as conversation without participants
        // won't be sent to current user
        return "Chat leer";
    } else {
        return (link?
            <NavLink to={`/profile/${user.get('slug')}`}>
                {user.get("nickname")}
            </NavLink>: user.get('nickname')
        );
    }
}

// Lives in the open conversation, not in the list: by the time the button is
// visible the member has seen who wrote and how much is there, which is what
// they need to decide. A list row shows only a name.
//
// Spending the gift must never happen on a stray tap, so the button only opens
// a confirmation and the unlock is sent after it is accepted.
const UnlockFreeButton = ({slug}) => {
    const dispatch = useDispatch();
    const [confirming, setConfirming] = useState(false);
    return (
        <>
            <Button size="small" compact primary className="chat-unlock-button"
                onClick={() => setConfirming(true)}
            >
                Dieses Gespräch gratis öffnen
            </Button>
            <Confirm
                open={confirming}
                header="Dein freies Gespräch"
                content={"Damit nutzt du dein freies Gespräch. Alle anderen " +
                         "Gespräche bleiben verschwommen. Das funktioniert nur, " +
                         "wenn diese Person ihr freies Gespräch ebenfalls hier " +
                         "einsetzt."}
                cancelButton="Abbrechen"
                confirmButton="Gratis öffnen"
                onCancel={() => setConfirming(false)}
                onConfirm={() => {
                    setConfirming(false);
                    dispatch(unlockFreeConversation(slug));
                }}
            />
        </>
    );
};

const ConversationList = ({className}) => {
    const conversations = useSelector(getConversations);
    const unreadCountMap = useSelector(getUnreadChatCount);
    const curUserId = useSelector(getAuthUserId);
    // TODO: empty list message
    return (
        <div {...{className}}>
            <Header attached="top" as="h3" className="chat-conv-header">
                Unterhaltungen
            </Header>
            <Menu vertical attached="top" className="chat-conv-list" fluid>
                {conversations.map(conv => {
                    const unreadCount = unreadCountMap.get(conv.get('slug'), 0) || 0;
                    const slug = conv.get('slug');
                    return (
                    <Menu.Item
                        as={NavLink}
                        key={slug}
                        to={`/chat/${slug}`}
                    >
                        {makeConversationTitle(conv, curUserId)}
                        {unreadCount > 0 ? <Label basic color="red">
                            {unreadCount}
                        </Label> : null}
                        {conv.get('locked') && !conv.get('unlockable') ?
                            <Label basic size="tiny">Abo nötig</Label> : null}
                    </Menu.Item>
                    );
                })}
            </Menu>
        </div>
    );
};

const ChatMessage = ({message: msg, rightUser, cid}) => {
    const dispatch = useDispatch();
    const right = msg.get('author', rightUser) === rightUser;
    const undelivered = msg.get("undelivered", false);
    // Refused by the server rather than lost on the way. Deliberately separate
    // from `undelivered`: this one must never offer a retry, because the answer
    // will be the same every time. The reason is already on screen, in the
    // notice above the message input.
    const refused = isPresent(msg.get("refused"));
    const inTransit = msg.has("transitKey") && !undelivered && !refused;
    // Server-side flag: the text below is already filler, not the real message.
    const masked = msg.get("masked", false);
    return (
        <div
            className={["ui", "chat-message", right?"right":""].join(' ')}
            onClick={()=>undelivered && dispatch(sendChatMessage(
                msg.get("text"), {conversationId: cid}, msg.get("transitKey")))
            }
        >
            <div className={["ui", "chat-text", masked?"masked":""].join(' ')}>
                {inTransit ? <Loader inline size="tiny" active/> : null}
                {msg.get('text')}
            </div>
            {undelivered ?
                <div className="ui chat-error">
                    <Icon name="warning circle"/> Möglicherweise
                    nicht zugestellt. Tippe, um erneut zu versenden.
                </div> :
             refused ?
                <div className="ui chat-error">
                    <Icon name="warning circle"/> Nicht gesendet.{" "}
                    {/* The advice has to match the reason. Telling somebody who
                        only needs a profile photo to take out a subscription sells
                        them something that will not help, and without the link it
                        is a dead end as well. Gated on the same list as the link,
                        deliberately: the sentence and the link must never drift
                        apart. */}
                    {SUBSCRIPTION_SOLVES.includes(msg.get("refused")) ? <>
                        Schließe ein Abo ab — damit erreichst du alle.{" "}
                        <a href="/subscribe">Jetzt abonnieren</a>
                    </> : null}
                </div> :
                <div className="ui chat-meta">{timeSince(msg.get('created_at'))}</div>
            }
        </div>
    );
};

// No subscription check any more: "you need a subscription to chat" stopped
// being true once every member got one free conversation.
const ConversationEmptyContent = () => {
    return (
        <Message>
            <Message.Header>Wähle ein Gespräch aus</Message.Header>
            <p>
                Du kannst im linken Bereich ein Gespräch auswählen und
                weiter mit der Person schreiben oder ein neues Gespräch
                über das Profil der jeweiligen Person anfangen.
            </p>
        </Message>
    );
}

const ConversationHeader = ({conversationId}) => {
    const conversation = useSelector(state => getConversation(state, conversationId));
    const curUserId = useSelector(getAuthUserId);
    const dispatch = useDispatch();
    const history = useHistory();

    const onDelete = async () => {
        // Optimistic UI: remove immediately so the chat disappears without a full refresh.
        dispatch(closeChatConversation(conversationId));
        dispatch(removeConversation(conversationId));
        history.push("/chat");
        dispatch(deleteChat(conversationId));
    }

    return (
        <Menu borderless fluid attached='top' className="chat-header">
            <Responsive maxWidth={Responsive.onlyMobile.maxWidth} as={React.Fragment}>
                <Menu.Item as={BackIcon} to="/chat/" />
            </Responsive>
            <Menu.Item header as='h3'>
                {isDefined(conversation) ?
                    makeConversationTitle(conversation, curUserId, true) :
                    null
                }
            </Menu.Item>
            <Menu.Item className="right">
                <ConfirmButton icon="trash" basic color="red" title="Löschen" 
                onClick={onDelete}
                />
            </Menu.Item>
        </Menu>
    );
};

const ConversationRecipientInput = ({slug, onChange}) => {
    const user = useSelector(state => getProfile(state, slug));
    const dispatch = useDispatch();
    useEffect(() => {
        if(!isDefined(user)) {
            dispatch(fetchProfile(slug));
        }
    }, [user]);
    if(!isDefined(user)) return null;
    const name = user.get('nickname', 'Unbekannt');
    return (
        <Menu borderless fluid attached='top' className="chat-header">
            <Menu.Item header as='h3'>
                {name}
            </Menu.Item>
        </Menu>
    );
};

const ConversationStream = ({conversationId}) => {
    const commentRef = useRef(null);
    const messages = useSelector(state => getConversationMessages(state, conversationId));
    const userId = useSelector(getAuthUserId);
    const dispatch = useDispatch();
    const isOnline = useSelector(getChatOnline);

    useEffect(() => {
        const ref = commentRef.current;
        if (!ref) return;
        try {
            ref.scrollTo({top: ref.scrollHeight, behavior: 'smooth'});
        } catch (e) {
            ref.scrollTo(0, ref.scrollHeight);
        }
        const lastMessageId = messages && messages.last() && messages.last().get("slug");
        if (lastMessageId) {
            dispatch(updateLastSeenMessage(conversationId, lastMessageId));
        }
    }, [messages, commentRef]);

    return (
        <Segment as={DivRef} attached className="chat-content" forwardedRef={commentRef}>
            {messages.map((m, i) => (
                <ChatMessage
                    message={m}
                    rightUser={userId}
                    key={m.get('slug')}
                    index={i}
                    cid={conversationId}
                />
            ))}
            {isOnline ? null : <div className="chat-offline">
                <Icon name="warning" />
                Es besteht keine Verbindung zu Kuscheldate mehr.<br />
                Bitte habe ein wenig Geduld, bis die Verbindung zurückkehrt.
            </div>}
        </Segment>
    );
};

const ConversationMessageInput = ({action, actionParams={}}) => {
    const [text, setText] = useState("");
    const dispatch = useDispatch();
    const sendMessage = useCallback((msg, params) => {
        dispatch(action(msg, params));
        // IMPROVE: save until delivery confirmed
        setText("");
    }, []);
    const rows = Math.max(1, text.split(/\r\n|\r|\n/).length);

    return (
        <Form className="bottom attached chat-input-form"
            onSubmit={()=>sendMessage(text, actionParams)}
        >
            <Input action fluid>
                <TextArea rows={rows} value={text} onInput={(e)=>setText(e.target.value)} />
                <Button primary icon='send' type="submit" />
            </Input>
        </Form>
    );
};

// What the member is told above the message input. A refusal the server just
// sent wins; otherwise, a conversation that is still blurred explains itself.
const ChatNotice = ({conversationId}) => {
    const error = useSelector(getChatError);
    const conversation = useSelector(
        state => getConversation(state, conversationId));
    const dispatch = useDispatch();

    // A refusal belongs to the conversation it happened in, so leaving clears it.
    useEffect(() => () => {
        dispatch(clearChatError());
    }, [conversationId]);

    // A refusal replaces the notice, so it has to carry whatever the notice was
    // offering. Without the button below, a member told to "öffne es gratis"
    // finds the button gone the moment they try to send — the advice stays on
    // screen and the means to follow it disappears.
    const unlockable = isDefined(conversation) && conversation.get('unlockable');

    if (isPresent(error)) {
        return (
            <Message warning attached className="chat-subscribe-hint">
                <p>
                    {chatErrorText(error)}{" "}
                    <a href="/subscribe">Jetzt abonnieren</a>
                </p>
                {unlockable ? <UnlockFreeButton slug={conversationId} /> : null}
            </Message>
        );
    }
    if (!isDefined(conversation) || !conversation.get('locked')) return null;

    // Concept section 4, case A. Nobody has answered yet, so nothing is hidden
    // from this member and nothing has been spent — the encouraging version of
    // the message, not the paywall one.
    if (conversation.get('awaiting_reply')) {
        return (
            <Message info attached className="chat-subscribe-hint">
                Dein erstes gegenseitiges Gespräch ist gratis. Sobald diese
                Person antwortet, könnt ihr hier frei lesen und schreiben.
            </Message>
        );
    }

    if (conversation.get('unlockable')) {
        return (
            <Message warning attached className="chat-subscribe-hint">
                <p>
                    Dieses Gespräch ist verschwommen. Du kannst es einmalig
                    gratis öffnen und dann frei lesen und schreiben.
                </p>
                <UnlockFreeButton slug={conversationId} />
            </Message>
        );
    }
    return (
        <Message warning attached className="chat-subscribe-hint">
            {conversation.get('partner_gone') ?
                "Diese Person nutzt ihr freies Gespräch bereits mit jemand " +
                "anderem und könnte dir hier nicht antworten. Dein freies " +
                "Gespräch bleibt dir erhalten." :
                "Dieses Gespräch ist verschwommen."}{" "}
            <a href="/subscribe">Jetzt abonnieren</a>
        </Message>
    );
};

const ConversationContent = ({className, slug: _slug}) => {
    const [slug, setSlug] = useState();
    const dispatch = useDispatch();
    const conversation = useSelector(state => getConversation(state, slug));
    const locked = isDefined(conversation) ?
        !!conversation.get('locked') : null;
    // Remembers which conversation the flag belonged to, not just the flag.
    const wasLocked = useRef({slug: null, locked: null});
    useEffect(() => {
        if(isPresent(slug)){
            dispatch(openChatConversation(slug));
        }
        return () => {
            if(isPresent(slug)){
                dispatch(closeChatConversation(slug));
            }
        };
    }, [slug]);
    useEffect(() => {
        const previous = wasLocked.current;
        wasLocked.current = {slug, locked};
        // A subscription opened while the conversation was still blurred holds
        // masked text and got no live stream, so spending the gift has to
        // re-subscribe.
        //
        // Only when THIS conversation went from blurred to readable. Comparing
        // the flag alone also fired when moving from a locked conversation to an
        // unlocked one — the previous conversation's value was still in the ref
        // — and it rebuilt a subscription that the effect above had only just
        // created. A message sent during that rebuild goes out on a subscription
        // the server has not confirmed, and ActionCable drops it in silence.
        if (previous.slug === slug && previous.locked === true &&
            locked === false && isPresent(slug)) {
            dispatch(openChatConversation(slug));
        }
    }, [slug, locked]);
    useEffect(() => {
        // Important: when route changes from /chat/:slug to /chat (no slug),
        // we must clear local state, otherwise the right pane keeps showing the old chat
        // until the user clicks another conversation.
        setSlug(isPresent(_slug) ? _slug : null);
    }, [_slug]);

    if (!isPresent(slug)) return null;

    return (
        <Comment.Group minimal className={["chat-conversation", className].filter(Boolean).join(" ")}>
            <ConversationHeader conversationId={slug} />
            <ChatNotice conversationId={slug} />
            <ConversationStream conversationId={slug} />
            <ConversationMessageInput action={sendChatMessage}
                actionParams={{conversationId: slug}}
            />
        </Comment.Group>
    );
};

// Shown while composing a first message: a member without a subscription is
// allowed to write, and should be told what it costs them — nothing.
const NewChatPromise = () => {
    const error = useSelector(getChatError);
    if (isPresent(error)) return null;
    return (
        <Message info attached className="chat-subscribe-hint">
            Dein erstes gegenseitiges Gespräch ist gratis. Anschreiben kostet
            dich nichts — dein freies Gespräch bleibt dir, bis jemand antwortet.
        </Message>
    );
};

const NewChatContent = ({presetUserId}) => {
    const [recipient, setRecipient] = useState(presetUserId);
    useEffect(() => {
        setRecipient(presetUserId);
    }, [presetUserId]);
    return (
        <Comment.Group minimal className="chat-conversation">
            <ConversationRecipientInput
                slug={recipient}
                onChange={setRecipient}
            />
            <ChatNotice />
            <NewChatPromise />
            <ConversationStream />
            <ConversationMessageInput
                action={createConversation}
                actionParams={{recipient}}
            />
        </Comment.Group>
    );
};

const MobileStackView = ({slug}) => {
    return (
        <div className="mobile-stack">
            <Route path="/chat/:slug"
                children={({match}) => (
                    <Transition visible={match !== null} animation="slide left">
                        <ConversationContent slug={match?.params?.slug} />
                    </Transition>
                )}
            />
            <Route path="/chat/" exact
                children={({match}) => (
                    <Transition visible={match !== null} animation="slide right">
                        <ConversationList />
                    </Transition>
                )}
            />
        </div>
    );
};

// No subscription gate here: a member without an active subscription may open
// an existing conversation and see the bubbles blurred. The server decides what
// text they get, and #speak still bounces them to /subscribe when they send.
const ChatPage = () => {
    const authed = useSelector(isAuthed);
    const {slug} = useParams(); // conversation slug
    if (!authed) {
        return <Redirect to="/" />;
    }
    return (
        <>
            <Responsive minWidth={Responsive.onlyTablet.minWidth}>
                <LogoSegment />
            </Responsive>
            <Grid columns={2} className="chat-page">
                <Grid.Row stretched>
                    <Responsive
                        minWidth={Responsive.onlyTablet.minWidth}
                        as={Grid.Column}
                        width={5}
                    >
                        <ConversationList />
                    </Responsive>
                    <Responsive
                        minWidth={Responsive.onlyTablet.minWidth}
                        as={Grid.Column}
                        width={11}
                    >
                        <Switch>
                            <Route path="/chat/:slug"
                                render={({match: {params: {slug}}}) =>
                                    <ConversationContent slug={slug} />
                                }
                            />
                            <Route path="/chat/"
                                render={() => <ConversationEmptyContent />}
                            />
                        </Switch>
                    </Responsive>
                    <Responsive maxWidth={Responsive.onlyMobile.maxWidth}
                        as={Grid.Column}
                        width={16}
                    >
                        <MobileStackView slug={slug} />
                    </Responsive>
                </Grid.Row>
            </Grid>
        </>
    );
};

export default ChatPage;

// No subscription gate: a member without one may start a conversation with
// their free gift. The server applies the profile check and the daily limit and
// explains a refusal in place, which is the whole point of the change.
const NewChatPage = () => {
    const authed = useSelector(isAuthed);
    const {slug} = useParams(); // prefilled user slug
    if (!authed) {
        return <Redirect to="/" />;
    }
    return (
        <>
            <Responsive minWidth={Responsive.onlyTablet.minWidth}>
                <LogoSegment />
            </Responsive>
            <Grid columns={2} className="chat-page">
                <Grid.Row stretched>
                    <Responsive
                        minWidth={Responsive.onlyTablet.minWidth}
                        as={Grid.Column}
                        width={5}
                    >
                        <ConversationList />
                    </Responsive>
                    <Responsive
                        minWidth={Responsive.onlyTablet.minWidth}
                        as={Grid.Column}
                        width={11}
                    >
                        <NewChatContent presetUserId={slug} />
                    </Responsive>
                    <Responsive {...Responsive.onlyMobile}
                        as={Grid.Column}
                        width={16}
                    >
                        <NewChatContent presetUserId={slug} />
                    </Responsive>
                </Grid.Row>
            </Grid>
        </>
    );
};

export {NewChatPage};
